import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileText, Download, Eye, ArrowClockwise, MagnifyingGlass, Receipt, Export, FileCsv, FileXls } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { listInvoices, downloadInvoice, viewInvoice, regenerateInvoice, exportInvoices } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

function formatMoney(v, currency = 'EUR') {
  const n = Number(v);
  if (!isFinite(n)) return '—';
  const cur = (currency || 'EUR').toUpperCase();
  const opts = { style: 'currency', currency: cur };
  if (cur === 'XPF') { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
  try { return new Intl.NumberFormat('fr-FR', opts).format(n); } catch { return `${n} ${cur}`; }
}

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportForm, setExportForm] = useState({ format: 'xlsx', date_from: '', date_to: '', territoire: '', advanced: false });
  const [exporting, setExporting] = useState(false);

  const load = async (silent = false) => {
    setLoading(true);
    try {
      const r = await listInvoices({ limit: 100 });
      setItems(r.data.items || []);
    } catch (e) {
      // Ignore 503 transitoire au premier chargement (pod démarrage)
      if (e?.response?.status !== 503 && !silent) toast.error('Erreur chargement factures');
      // Retry en cas de 503
      if (e?.response?.status === 503) {
        setTimeout(() => load(true), 1500);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((inv) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (inv.number || '').toLowerCase().includes(s) ||
      (inv.reference || '').toLowerCase().includes(s) ||
      (inv.client?.name || '').toLowerCase().includes(s) ||
      (inv.client?.email || '').toLowerCase().includes(s)
    );
  });

  const doDownload = async (inv) => {
    try {
      const r = await downloadInvoice(inv.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur téléchargement'); }
  };

  const doView = async (inv) => {
    try {
      const r = await viewInvoice(inv.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Erreur ouverture'); }
  };

  const doRegen = async (inv) => {
    if (!inv.payment_link_id) return;
    try {
      await regenerateInvoice(inv.payment_link_id);
      toast.success('Facture régénérée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur régénération');
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const params = { format: exportForm.format, advanced: exportForm.advanced };
      if (exportForm.date_from) params.date_from = exportForm.date_from;
      if (exportForm.date_to) params.date_to = exportForm.date_to;
      if (exportForm.territoire) params.territoire = exportForm.territoire;
      const r = await exportInvoices(params);
      const ext = exportForm.format === 'xlsx' ? 'xlsx' : 'csv';
      const mime = exportForm.format === 'xlsx'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url; a.download = `factures_export_${ts}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
      setExportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur export');
    } finally { setExporting(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Factures"
        subtitle="Émission, suivi et rapprochement des factures coopératives"
        icon={<Receipt size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#D5007D]"
        accents={['#80C51F', '#00AEEF']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Factures
          </h1>
          <p className="text-[#5E6D66] mt-1">Générées automatiquement à la confirmation d'un paiement Stripe.</p>
        </div>
        <Button variant="outline" onClick={load} className="border-[#E8E6E1]" data-testid="reload-invoices-btn">
          <ArrowClockwise size={16} className="mr-2" /> Actualiser
        </Button>
        <Button onClick={() => setExportOpen(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136] ml-2" data-testid="open-export-btn">
          <Export size={16} className="mr-2" /> Export comptable
        </Button>
      </div>

      <Card className="border-[#E8E6E1]">
        <CardContent className="p-4">
          <div className="relative mb-4">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E6D66]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher numéro, client, référence..."
              className="pl-9 border-[#E8E6E1]"
              data-testid="invoices-search"
            />
          </div>

          {loading ? (
            <div className="p-6 text-center text-[#5E6D66]"><Loader2 className="mx-auto animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-[#5E6D66]">
              <Receipt size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucune facture{q ? ' correspondant à la recherche' : ''}.</p>
              {!q && <p className="text-xs mt-1">Les factures sont générées automatiquement dès qu'un lien Stripe est payé.</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E8E6E1] text-left text-xs uppercase tracking-wider text-[#5E6D66]">
                    <th className="py-2 pr-3">N°</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Client</th>
                    <th className="py-2 pr-3">Référence</th>
                    <th className="py-2 pr-3">Montant</th>
                    <th className="py-2 pr-3">Statut</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => (
                    <tr key={inv.id} className="border-b border-[#E8E6E1] hover:bg-[#F9F8F5]" data-testid={`invoice-row-${inv.id}`}>
                      <td className="py-2 pr-3 font-mono text-xs">{inv.number}</td>
                      <td className="py-2 pr-3 text-xs text-[#5E6D66]">{inv.issued_at ? inv.issued_at.slice(0, 10) : '-'}</td>
                      <td className="py-2 pr-3">
                        <div>{inv.client?.name || '-'}</div>
                        {inv.client?.email && <div className="text-[10px] text-[#5E6D66]">{inv.client.email}</div>}
                      </td>
                      <td className="py-2 pr-3 text-xs">{inv.reference}</td>
                      <td className="py-2 pr-3 font-semibold">{formatMoney(inv.paid_amount, (inv.currency || 'eur').toUpperCase())}</td>
                      <td className="py-2 pr-3">
                        <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">{inv.status || 'paid'}</Badge>
                        {inv.email_sent_at && (
                          <Badge className="ml-1 bg-blue-100 text-blue-800 text-[10px]">Envoyée</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" title="Aperçu" onClick={() => doView(inv)} data-testid={`view-invoice-${inv.id}`}>
                            <Eye size={16} />
                          </Button>
                          <Button size="icon" variant="ghost" title="Télécharger" onClick={() => doDownload(inv)} data-testid={`download-invoice-${inv.id}`}>
                            <Download size={16} />
                          </Button>
                          <Button size="icon" variant="ghost" title="Régénérer" onClick={() => doRegen(inv)} data-testid={`regen-invoice-${inv.id}`}>
                            <ArrowClockwise size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Export size={20} /> Export comptable des factures</DialogTitle>
            <DialogDescription>Choisissez la période, le territoire et le format d'export.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date de début</Label>
                <Input type="date" value={exportForm.date_from} onChange={(e) => setExportForm({ ...exportForm, date_from: e.target.value })} className="border-[#E8E6E1]" data-testid="export-date-from" />
              </div>
              <div>
                <Label className="text-xs">Date de fin</Label>
                <Input type="date" value={exportForm.date_to} onChange={(e) => setExportForm({ ...exportForm, date_to: e.target.value })} className="border-[#E8E6E1]" data-testid="export-date-to" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Territoire (code ISO 2 lettres, optionnel)</Label>
              <Input value={exportForm.territoire} onChange={(e) => setExportForm({ ...exportForm, territoire: e.target.value.toUpperCase() })} placeholder="Ex: GP, FR, NC" maxLength={2} className="border-[#E8E6E1] font-mono" data-testid="export-territoire" />
              <p className="text-[10px] text-[#5E6D66] mt-1">Laissez vide pour exporter tous territoires confondus.</p>
            </div>
            <div>
              <Label className="text-xs">Format</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setExportForm({ ...exportForm, format: 'xlsx' })}
                  className={`flex-1 p-3 rounded-md border transition-colors ${exportForm.format === 'xlsx' ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                  data-testid="export-format-xlsx"
                >
                  <FileXls size={20} className="mx-auto mb-1" />
                  <span className="text-xs font-medium">Excel (.xlsx)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setExportForm({ ...exportForm, format: 'csv' })}
                  className={`flex-1 p-3 rounded-md border transition-colors ${exportForm.format === 'csv' ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                  data-testid="export-format-csv"
                >
                  <FileCsv size={20} className="mx-auto mb-1" />
                  <span className="text-xs font-medium">CSV (;)</span>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 border border-[#E8E6E1] rounded-md bg-[#F9F8F5]">
              <input
                type="checkbox"
                id="advanced-cols"
                checked={exportForm.advanced}
                onChange={(e) => setExportForm({ ...exportForm, advanced: e.target.checked })}
                data-testid="export-advanced"
              />
              <Label htmlFor="advanced-cols" className="text-xs cursor-pointer">Inclure les colonnes techniques (Dossier ID, Contact ID, Organisation ID, Payment Link ID)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)} className="border-[#E8E6E1]">Annuler</Button>
            <Button onClick={doExport} disabled={exporting} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="export-submit-btn">
              {exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
              Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
